import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  MessageCircle, Lock, Shield, UserCheck, ArrowLeft, ArrowRight, Sparkles,
  Mail, Share2, Copy, Check, TrendingUp, Phone,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/patient-protected-chat';
const SHARE_MESSAGE = (
  `New on DocSite — your patient chat is now spam-proof and lead-generating.\n\n` +
  `Every chat starts with a mobile verification. Your existing patients skip it. Strangers asking for free advice repeatedly hit a sign-up wall. ` +
  `New chats land in a brand-new Chat Inbox tab; existing patients merge into their Patient Log timeline automatically.\n\n` +
  `Read more: ${SHARE_URL}`
);

export default function PatientProtectedChatLaunch() {
  const [copied, setCopied] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent("DocSite — your chat is now spam-proof")}&body=${encodeURIComponent(SHARE_MESSAGE)}`;
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'DocSite — patient-protected chat', text: SHARE_MESSAGE, url: SHARE_URL }); }
      catch { /* cancelled */ }
    } else { copyLink(); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(SHARE_URL); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard denied */ }
  };

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>DocSite — Your patient chat is now spam-proof and lead-generating</title>
        <meta name="description" content="Every patient chat now starts with a mobile verification. Existing patients skip it. New chats land in a Chat Inbox; known patients merge into Patient Log. Set your own chat limits per patient." />
        <meta name="robots" content="index, follow" />
        <meta name="keywords" content="doctor patient chat India, secure patient messaging, mobile verified chat, doctor anti-spam, patient chat limit, DocSite chat inbox" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="DocSite — Your patient chat is now spam-proof" />
        <meta property="og:description" content="OTP-verified chat, existing patient auto-skip, doctor-set chat caps, separate Chat Inbox for new leads. Built so top doctors keep their time their own." />
        <meta property="og:url" content={SHARE_URL} />
        <meta property="article:published_time" content="2026-02-14" />
        <link rel="canonical" href={SHARE_URL} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'NewsArticle',
          headline: 'Your patient chat is now spam-proof — and brings you real leads',
          description: 'A 4-step verified chat that auto-routes new leads to Chat Inbox and known patients into Patient Log, with doctor-configurable per-patient session caps.',
          datePublished: '2026-02-14',
          author: { '@type': 'Organization', name: 'DocSite' },
          publisher: { '@type': 'Organization', name: 'DocSite' },
          mainEntityOfPage: SHARE_URL,
        })}</script>
      </Helmet>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8 print:p-0">
        <Link to="/whats-new" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 print:hidden">
          <ArrowLeft size={14} /> Back to What's New
        </Link>

        <div className="mt-4 rounded-3xl bg-white border border-slate-200 px-6 sm:px-10 py-8 sm:py-12 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <span className="text-[10px] uppercase tracking-widest font-bold text-rose-700 bg-rose-50 px-2 py-1 rounded">Just Launched · Feb 14, 2026</span>
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">For doctors</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Your patient chat is now spam-proof — and brings you real leads.
          </h1>
          <p className="mt-4 text-base text-slate-700 leading-relaxed">
            No more random pings from people you don't know. Every new chat on your DocSite page now starts with a quick mobile verification.
            <b> Your existing patients skip it automatically.</b> And if someone has already chatted you up once, they're nudged to sign up before doing it again — so doctor-shoppers don't eat into your day.
          </p>

          <hr className="my-7 border-slate-200" />

          <h2 className="text-xl font-bold text-slate-900 mb-4">What's new</h2>
          <ul className="space-y-4 text-sm text-slate-700 leading-relaxed">
            <li className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center shrink-0"><UserCheck size={18} /></div>
              <div>
                <p className="font-semibold text-slate-900">Mobile-verified chat (skipped for your existing patients)</p>
                <p className="mt-0.5">Patients pick <i>Patient</i> or <i>Patient's relative</i>, enter their mobile, receive a one-time code via SMS (with email fallback). If the mobile is already in your Patient Log, the code step is auto-skipped. After verification we also ask whose number it is — patient's own or relative's — so you always have the right contact.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0"><MessageCircle size={18} /></div>
              <div>
                <p className="font-semibold text-slate-900">Brand-new "Chat Inbox" tab in your dashboard</p>
                <p className="mt-0.5">Chats from people you've <i>never</i> seen before land in this tab — your lead funnel. You see their full mobile (so you can call back) inside the chat; the inbox list only shows the last 4 digits to keep things tidy.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center shrink-0"><Sparkles size={18} /></div>
              <div>
                <p className="font-semibold text-slate-900">Existing patients' chats merge into Patient Log automatically</p>
                <p className="mt-0.5">If a patient who's already in your Patient Log starts a chat, it shows up right alongside their walk-in, booking and call history — no manual filing.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-rose-50 text-rose-700 flex items-center justify-center shrink-0"><Lock size={18} /></div>
              <div>
                <p className="font-semibold text-slate-900">Anti-abuse wall + per-patient chat caps</p>
                <p className="mt-0.5">Anyone who's already chatted with you once must sign in or register a patient profile to continue. Signed-in patients get plan-based caps: <b>Free 3 chats</b>, <b>Gold 5 chats</b>, <b>Turbo unlimited</b> — per doctor. You can also set your own stricter cap in Calls &amp; Chat → "How many chats per patient?".</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0"><TrendingUp size={18} /></div>
              <div>
                <p className="font-semibold text-slate-900">Conversion banner — see how chat turns into patients</p>
                <p className="mt-0.5">A new green banner at the top of your Chat Inbox shows you, every month, how many new chats turned into actual patients (i.e., they now appear in your Patient Log). It's a tiny ROI dashboard that grows as you reply faster.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0"><Shield size={18} /></div>
              <div>
                <p className="font-semibold text-slate-900">Doctor's contact stays private</p>
                <p className="mt-0.5">The patient never sees your phone or email anywhere in the chat surface — only your name. Their phone is encrypted at rest and only visible to you when you open the thread.</p>
              </div>
            </li>
          </ul>

          <hr className="my-7 border-slate-200" />

          <h2 className="text-xl font-bold text-slate-900 mb-3">Why it matters</h2>
          <p className="text-sm text-slate-700 leading-relaxed">
            Top doctors get a flood of "quick question" pings on WhatsApp every day. Most never convert into real visits. With this update,
            patients <i>have to</i> verify themselves and sign up — which means the people who reach you are serious, the people who already trust you skip friction, and your time stays in your hands.
          </p>

          <div className="mt-7 rounded-2xl bg-slate-50 border border-slate-200 p-5 print:hidden">
            <p className="text-sm font-semibold text-slate-900 mb-2">Tell a colleague</p>
            <p className="text-xs text-slate-600 mb-3">Share this update with another doctor who's tired of WhatsApp time-passers.</p>
            <div className="flex flex-wrap gap-2">
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold px-3 py-2 hover:bg-emerald-700">
                <Phone size={12} /> WhatsApp
              </a>
              <a href={mailHref} className="inline-flex items-center gap-1.5 rounded-full bg-sky-600 text-white text-xs font-semibold px-3 py-2 hover:bg-sky-700">
                <Mail size={12} /> Email
              </a>
              <button onClick={nativeShare} className="inline-flex items-center gap-1.5 rounded-full bg-slate-800 text-white text-xs font-semibold px-3 py-2 hover:bg-slate-900">
                <Share2 size={12} /> Share
              </button>
              <button onClick={copyLink} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-300 text-slate-700 text-xs font-semibold px-3 py-2 hover:bg-slate-50">
                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy link</>}
              </button>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3 print:hidden">
            <Link to="/login" className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-sm font-semibold px-5 py-2.5 hover:bg-slate-800">
              Open my Chat Inbox <ArrowRight size={14} />
            </Link>
            <Link to="/whats-new" className="text-sm text-slate-500 hover:text-slate-800">More updates →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
